import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { Box, CircularProgress, IconButton, Link, Switch } from "@mui/material";
import moment from "moment";
import { AUDIO_SOURCES } from "./AudioService";
import { formatAudioBytes } from "./audioUtils";

function sourceMeta(label) {
    const key = String(label || "").trim().toLowerCase();
    if (!key) return null;
    return AUDIO_SOURCES.find((s) => s.label.toLowerCase() === key) || null;
}

function extensionFromPath(source) {
    const path = String(source || "").split(/[?#]/)[0];
    const name = path.slice(path.lastIndexOf("/") + 1).toLowerCase();
    const dot = name.lastIndexOf(".");
    if (dot < 0 || dot === name.length - 1) return "";
    return name.slice(dot + 1);
}

function getFileType(row) {
    const fromName = extensionFromPath(row?.originalFilename || row?.fileName);
    if (fromName) return fromName.toUpperCase();
    const fromKey = extensionFromPath(row?.s3Key || row?.url);
    if (fromKey) return fromKey.toUpperCase();
    const ct = String(row?.contentType || "").toLowerCase();
    if (ct.includes("mpeg") || ct.includes("mp3")) return "MP3";
    if (ct.includes("wav")) return "WAV";
    if (ct.includes("caf") || ct.includes("coreaudio")) return "CAF";
    return "";
}

export function getColumns({
    onEditClick,
    onToggleEnable,
    /** Starts or stops in-page playback for the row. */
    onTogglePlay,
    /** Downloads the stored file using the original basename. */
    onDownloadClick,
    /** Opens confirm-and-delete for the row. */
    onDeleteClick,
    /** Row id currently playing, if any. */
    playingId = null,
    /** Row id whose audio is still buffering. */
    loadingPlayId = null,
    /** Row id whose download is in progress. */
    downloadingId = null,
}) {
    return [
        {
            field: "name",
            headerName: "Name",
            flex: 1,
            minWidth: 160,
        },
        {
            field: "fileType",
            headerName: "File type",
            width: 100,
            sortable: false,
            valueGetter: (_value, row) => getFileType(row),
        },
        {
            field: "category",
            headerName: "Category",
            width: 100,
        },
        {
            field: "source",
            headerName: "Source",
            width: 140,
            renderCell: (params) => {
                const label = params.value ? String(params.value) : "";
                if (!label) return null;
                const known = sourceMeta(label);
                if (known?.url) {
                    return (
                        <Link
                            href={known.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            underline="hover"
                        >
                            {known.label}
                        </Link>
                    );
                }
                return label;
            },
        },
        {
            field: "enable",
            headerName: "Enabled",
            width: 90,
            sortable: false,
            renderCell: (params) => (
                <Box
                    sx={{ display: "flex", alignItems: "center", height: "100%" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Switch
                        checked={Boolean(params.value)}
                        size="small"
                        onChange={(e) => onToggleEnable?.(params.row, e.target.checked)}
                    />
                </Box>
            ),
        },
        {
            field: "sizeBytes",
            headerName: "Size",
            width: 90,
            valueFormatter: (value) => formatAudioBytes(value),
        },
        {
            field: "createdAt",
            headerName: "Created",
            width: 160,
            valueFormatter: (value) => (value ? moment(value).format("YYYY-MM-DD HH:mm") : ""),
        },
        {
            field: "actions",
            headerName: "Action",
            width: 168,
            sortable: false,
            renderCell: (params) => {
                const isPlaying = playingId === params.row.id;
                const isBuffering = loadingPlayId === params.row.id;
                const isDownloading = downloadingId === params.row.id;
                return (
                    <Box
                        sx={{ display: "flex", alignItems: "center", height: "100%" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {params.row.url ? (
                            <IconButton
                                size="small"
                                color={isPlaying ? "primary" : "default"}
                                aria-label={`${isPlaying ? "Stop" : "Play"} ${params.row.name || "audio"}`}
                                onClick={() => onTogglePlay?.(params.row)}
                            >
                                {isBuffering ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : isPlaying ? (
                                    <StopIcon fontSize="small" />
                                ) : (
                                    <PlayArrowIcon fontSize="small" />
                                )}
                            </IconButton>
                        ) : null}
                        {params.row.url ? (
                            <IconButton
                                size="small"
                                aria-label={`Download ${params.row.name || "audio"}`}
                                disabled={isDownloading}
                                onClick={() => onDownloadClick?.(params.row)}
                            >
                                {isDownloading ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : (
                                    <DownloadIcon fontSize="small" />
                                )}
                            </IconButton>
                        ) : null}
                        <IconButton
                            size="small"
                            aria-label={`Edit ${params.row.name || "audio"}`}
                            onClick={() => onEditClick(params.row)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            color="error"
                            aria-label={`Delete ${params.row.name || "audio"}`}
                            onClick={() => onDeleteClick?.(params.row)}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                );
            },
        },
    ];
}
